import { useState, useEffect, useContext } from 'react'

import { SearchInput } from '../../common/components/search-input'

import { TextInput } from '../../common/components/text-input'
import { Textarea } from '../../common/components/textarea'
import { Select } from '../../common/components/select'

import { REPORT_TYPES_LABEL, REPORT_VIEWER_MODE, REPORT_TYPES_SELECT_OPTIONS } from '../constants'
import { useReportTocContext } from '../contexts/report-toc.context'
import { TocList } from '../toc-list'

import { Footer } from './footer'
import { useStyles } from './page-list.style'
import { ExecutiveSummaryLink } from '../executive-summary-link'
import { isMobile } from '#base/js/common/services/is-mobile.service'

import { FloatingBackLink } from '../../common/components/floating-back-link'
import { RouterContext } from '../../common/contexts/routerContext'
import { httpService } from '../../common/services/http.service'

const DATE_FORMAT = 'MMMM D, YYYY'

export const PageList = function ({ report: reportParam, mode, isEditable = false }) {
  const classes = useStyles()
  const routerContext = useContext(RouterContext)

  const reportTocContext = useReportTocContext() || {}
  const report = isEditable ? reportTocContext.report : reportParam

  const [uncategorizedGroup, setUncategorizedGroup] = useState(report.groups && report.groups.find((group) => !group.id))
  const [allGroups, setAllGroups] = useState(report.groups)
  const [groups, setGroups] = useState(allGroups.filter((group) => !!group.id))
  const [title, setTitle] = useState(report.title)
  const [reportType, setReportType] = useState(REPORT_TYPES_SELECT_OPTIONS.find((option) => (option.value === report.type)))
  const [description, setDescription] = useState(report.description)

  useEffect(() => {
    setAllGroups(report.groups)
    setGroups(report.groups.filter((group) => !!group.id))
    setUncategorizedGroup(report.groups && report.groups.find((group) => !group.id))
  }, [report.groups])

  function filterPages (event) {
    const userInput = event.currentTarget.value.toLowerCase()
    const filteredGroups = allGroups.reduce((acc, { pages, ...group }) => {
      const filteredPages = pages.filter((page) => (
        (page.title || '').toLowerCase().includes(userInput)
      ))
      if (filteredPages.length > 0) {
        acc.push({ pages: filteredPages, ...group })
      }
      return acc
    }, [])
    setGroups(filteredGroups.filter((group) => !!group.id))
  }

  function updateTitle (event) {
    const title = event.target.value
    setTitle(title)
    reportTocContext.setNewTitle(title)
  }

  function updateReportType (selected) {
    setReportType(selected)
    reportTocContext.setNewType(selected.value)
  }

  function updateDescription (event) {
    const description = event.target.value
    setDescription(description)
    reportTocContext.setNewDescription(description)
  }

  return (
    <>
      <div className={`${classes.splitPage} ${mode === REPORT_VIEWER_MODE.READ ? 'full-view-height' : ''}`}>
        <div className={classes.leftSide}>
          <div className={classes.leftSideContent}>
            {isEditable
              ? <>
                <div className={classes.reportTypeEdit} data-qa="page-list-type">
                  <Select className={classes.versionSelector} options={REPORT_TYPES_SELECT_OPTIONS} value={reportType} onChange={updateReportType} />
                </div>
                <div className={classes.reportTitleEdit}>
                  <TextInput value={title} onChange={updateTitle} className={classes.titleTextField} placeholder="required" autoFocus data-qa="page-list-title" />
                </div>
                <div className={classes.published}>Updated: {report.publishedOrUpdated.format(DATE_FORMAT)}</div>
                <div>
                  <ExecutiveSummaryLink report={report} />
                </div>
                <div className={classes.reportDescriptionEdit}>
                  <Textarea value={description} onChange={updateDescription} placeholder="description" data-qa="page-list-description" />
                </div>
              </>
              : <>
                <div className={classes.reportType} data-qa="page-list-type-label">{REPORT_TYPES_LABEL[report.type]}</div>
                <div className={classes.title} data-qa="page-list-title-label">{report.title}</div>
                <div className={classes.published}>Updated: {report.publishedOrUpdated.format(DATE_FORMAT)}</div>
                <div className={classes.executiveSummaryLink}>
                  <ExecutiveSummaryLink report={report} />
                </div>
                {report.description &&
                    <div className={classes.summary} data-qa="page-list-desc-label">
                      {report.description}
                    </div>
                }
              </>
            }
          </div>
          <div className={classes.footer}>
            <Footer />
          </div>
        </div>
        <div className={classes.rightSide}>
          {isEditable
            ? <>
              <div className={classes.tocContainer}>
                <div className={classes.toc}>Table of Contents</div>
              </div>
              <TocList report={report} filteredToc={groups} isEditable uncategorizedGroup={uncategorizedGroup} />
            </>
            : <>
              <div className={classes.tocContainer}>
                <div className={classes.toc}>Table of Contents</div>
                <div className={classes.tocSearch}><SearchInput onChange={filterPages} autoFocus={!isMobile(navigator)}/></div>
              </div>
              {groups.length > 0
                ? <TocList report={report} filteredToc={groups} />
                : <div>No matching pages. Clear the filter to see more.</div>
              }
            </>
          }
        </div>
        <div className={classes.footerMobile}>
          <Footer />
        </div>
      </div>
      {mode === REPORT_VIEWER_MODE.READ && routerContext && !httpService.isStandalone &&
        <div>
          <div className={classes.backLink}>
            <FloatingBackLink to={routerContext.getReportListRoute(report)} />
          </div>
        </div>
      }
    </>
  )
}
